import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { auth } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { getLanguage, isSupported } from './languages';
import { resolveInitialLang } from './translate';
import { LanguageContext, useLang, useT } from './langContext';

const STORAGE_KEY = 'sg.lang';

const readStored = () => { try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; } };
const writeStored = (v) => { try { localStorage.setItem(STORAGE_KEY, v); } catch (e) { /* private mode */ } };

export function LanguageProvider({ children }) {
  const { currentUser, userLang } = useAuth() || {};
  const [lang, setLangState] = useState(() => resolveInitialLang({
    storedLang: readStored(), navigatorLangs: typeof navigator !== 'undefined' ? navigator.languages : [],
  }));

  // Profile wins once it loads (follows the user across devices). Also
  // persists it to localStorage so tStatic-based code outside this
  // provider's tree (e.g. AuthContext itself, which sits above it) agrees
  // with the profile instead of falling back to the browser/default guess.
  useEffect(() => {
    if (isSupported(userLang) && userLang !== lang) {
      setLangState(userLang);
      writeStored(userLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    auth.languageCode = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    setLangState(next);
    writeStored(next);
    if (currentUser) {
      updateDoc(doc(getFirestore(), 'users', currentUser.uid), { lang: next })
        .catch((e) => console.warn('lang profile save failed:', e));
    }
  }, [currentUser]);

  const value = useMemo(() => ({ lang, setLang, dateLocale: getLanguage(lang).dateLocale }), [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// Re-exported so every existing `import { useLang, useT } from
// '../i18n/LanguageContext'` keeps working unchanged.
export { useLang, useT };
